// MF-566 — Card 'subject-grades': the "Subjects Grades Results" block of the Belmont dashboard. ONE
// card that works as a pair: on the left every subject (name, course mark, bar, a list button); a click
// on a subject shows ITS markbook — course marks and tasks, one per row — on the right. The server
// sends every markbook column of the year; the card groups them, so choosing a subject asks nothing.
// Rows: { subject, markbook_name, kind: 'Course Mark' | 'Course Grade' | 'Task Percentage', mark, grade }.
import { useEffect, useMemo, useState } from 'react'
import { Box, ButtonBase, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faList } from '@fortawesome/pro-light-svg-icons'
import { SUMMARY_PALETTE } from '../../../Dashboard/StudentSummary/summary.palette'
import { fmtValue } from '../boardTheme'
import { benchmarkColor } from '../boardPalettes'

const TRACK = '#dfe3e8'

/** A subject can have several Course Marks ("Semester 1…", "Semester 2…", "Final…"): the headline one is
 *  the Final if there is one, else the latest semester. Never an average of them. */
export const headlineMark = (courseMarks = []) => {
  if (!courseMarks.length) return null
  const final = courseMarks.find((m) => /final/i.test(m.markbook_name || ''))
  if (final) return final
  const num = (m) => Number((String(m.markbook_name || '').match(/\d+/) || [0])[0])
  return [...courseMarks].sort((a, b) => num(b) - num(a))[0]
}

/** rows → [{ subject, headline, grade, items[] }] sorted by headline mark, best first (no mark: last). */
export const groupSubjects = (rows = []) => {
  const by = new Map()
  for (const r of rows) {
    if (!by.has(r.subject)) by.set(r.subject, { subject: r.subject, marks: [], grade: null, items: [] })
    const g = by.get(r.subject)
    if (r.kind === 'Course Grade') {
      g.grade = r.grade || g.grade
      continue
    }
    if (r.kind === 'Course Mark') g.marks.push(r)
    g.items.push(r)
  }
  return [...by.values()]
    .map((g) => ({ ...g, headline: headlineMark(g.marks) }))
    .sort((a, b) => (b.headline?.mark ?? -1) - (a.headline?.mark ?? -1) || a.subject.localeCompare(b.subject))
}

const SubjectGradesCard = ({ data }) => {
  const decimals = data?.meta?.card?.options?.decimals ?? 1
  const subjects = useMemo(() => groupSubjects(data?.rows || []), [data])
  const [picked, setPicked] = useState(null)
  // keep the choice while it exists; otherwise (new student, new data) go back to the first subject
  useEffect(() => {
    if (!subjects.some((s) => s.subject === picked)) setPicked(subjects[0]?.subject || null)
  }, [subjects, picked])
  const current = subjects.find((s) => s.subject === picked)

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, height: '100%', minHeight: 0 }}>
      <Box sx={{ flex: '1 1 260px', minWidth: 0, overflowY: 'auto', maxHeight: '100%' }}>
        {subjects.map((s, i) => {
          const value = s.headline?.mark
          const active = s.subject === picked
          return (
            <Box
              key={s.subject}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1,
                px: 0.5,
                borderBottom: i < subjects.length - 1 ? `1px solid ${SUMMARY_PALETTE.grid}` : 'none',
                bgcolor: active ? '#f1f6fb' : 'transparent',
                borderRadius: '8px'
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}
                >
                  <Typography sx={{ fontSize: 14, color: SUMMARY_PALETTE.ink, overflowWrap: 'anywhere' }}>
                    {s.subject}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 600,
                      color:
                        value === undefined || value === null ? SUMMARY_PALETTE.muted : benchmarkColor(value),
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {value === undefined || value === null ? '–' : fmtValue(value, decimals)}
                    {s.grade ? (
                      <Box
                        component='span'
                        sx={{
                          ml: 1,
                          px: 0.75,
                          borderRadius: '10px',
                          fontSize: 11,
                          bgcolor: '#e3ebf6',
                          color: SUMMARY_PALETTE.ink
                        }}
                      >
                        {s.grade}
                      </Box>
                    ) : null}
                  </Typography>
                </Box>
                <Box sx={{ height: 8, borderRadius: 4, bgcolor: TRACK, mt: 0.75, overflow: 'hidden' }}>
                  {value !== undefined && value !== null ? (
                    <Box
                      sx={{
                        height: '100%',
                        width: `${Math.max(0, Math.min(100, value))}%`,
                        bgcolor: benchmarkColor(value),
                        borderRadius: 4
                      }}
                    />
                  ) : null}
                </Box>
              </Box>
              <ButtonBase
                aria-label={`Show the markbook of ${s.subject}`}
                aria-pressed={active}
                onClick={() => setPicked(s.subject)}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  color: active ? SUMMARY_PALETTE.good : SUMMARY_PALETTE.muted
                }}
              >
                <FontAwesomeIcon icon={faList} />
              </ButtonBase>
            </Box>
          )
        })}
      </Box>

      <Box
        sx={{
          flex: '1 1 260px',
          minWidth: 0,
          overflowY: 'auto',
          maxHeight: '100%',
          pl: 2,
          borderLeft: `1px solid ${SUMMARY_PALETTE.grid}`
        }}
      >
        {current ? (
          <>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: SUMMARY_PALETTE.ink, mb: 1 }}>
              {current.subject} Markbook Results
            </Typography>
            {current.marks.length ? (
              <Box sx={{ mb: 1.5 }}>
                {current.marks.map((m, i) => (
                  <Box
                    key={`${m.markbook_name}-${i}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      py: 1,
                      px: 1,
                      bgcolor: '#f1f6fb',
                      borderRadius: '8px',
                      mb: 0.5
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: SUMMARY_PALETTE.ink }}>
                      {m.markbook_name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: benchmarkColor(m.mark),
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {fmtValue(m.mark, decimals)}
                    </Typography>
                  </Box>
                ))}
                <Typography sx={{ fontSize: 11, color: SUMMARY_PALETTE.faint, px: 1 }}>
                  Calculated by the school’s markbook (weighted): not the average of the tasks below.
                </Typography>
              </Box>
            ) : null}
            {current.items
              .filter((m) => m.kind !== 'Course Mark')
              .map((m, i) => (
                <Box
                  key={`${m.markbook_name}-${i}`}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 1,
                    borderBottom: `1px solid ${SUMMARY_PALETTE.grid}`
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.ink, overflowWrap: 'anywhere' }}>
                    {m.markbook_name}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: benchmarkColor(m.mark), whiteSpace: 'nowrap' }}>
                    {fmtValue(m.mark, decimals)}
                  </Typography>
                </Box>
              ))}
          </>
        ) : null}
      </Box>
    </Box>
  )
}

export default SubjectGradesCard
